import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import ProjectCard from "@/components/projects/ProjectCard";
import { FileText, Search } from "lucide-react";
import { useJobStore } from "@/store/jobStore";
import { useUserStore } from "@/store/userStore";
import { debounce } from "@/lib/utils";
import { CATEGORIES, Skills } from "@/utils/constant";
import { Pagination } from "@/components/ui/pagination-component";

const Projects: React.FC = () => {
  const ref = useRef(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [fetchDataProps, setFetchDataProps] = useState({
    search: "",
    skillFilter: "",
    category: "",
    budget: "",
    PricingDetails: "",
    page: 1,
    limit: 10,
  });

  const { profile, fetchAllJobProfile, fetchJobProfileById, fetchAllProject } = useJobStore();
  const { user } = useUserStore();

  // Initialize projects with icons
  // useEffect(() => {
  //   const projectsWithIcons = projectData.map(project => ({
  //     ...project,
  //     icon: <Briefcase size={20} />
  //   }));
  //   setProjects(projectsWithIcons);
  //   setFilteredProjects(projectsWithIcons);
  // }, []);

  // Handle filtering and sorting
  // useEffect(() => {
  //   let result = [...projects];

  //   // Apply status filter
  //   if (statusFilter !== 'all') {
  //     const statusMap: Record<string, string[]> = {
  //       'active': ['In Progress', 'Planning'],
  //       'completed': ['Completed'],
  //       'high-priority': ['High'],
  //     };

  //     if (statusFilter === 'high-priority') {
  //       result = result.filter(project => project.priority === 'High');
  //     } else {
  //       result = result.filter(project =>
  //         statusMap[statusFilter]?.includes(project.status)
  //       );
  //     }
  //   }

  //   // Apply search
  //   if (searchTerm) {
  //     const term = searchTerm.toLowerCase();
  //     result = result.filter(project =>
  //       project.title.toLowerCase().includes(term) ||
  //       project.description.toLowerCase().includes(term) ||
  //       project.client.toLowerCase().includes(term) ||
  //       project.skills.some(skill => skill.toLowerCase().includes(term))
  //     );
  //   }

  //   // Apply sorting
  //   switch (sortBy) {
  //     case 'recent':
  //       result.sort((a, b) => new Date(b.postedDate).getTime() - new Date(a.postedDate).getTime());
  //       break;
  //     case 'budget-high':
  //       result.sort((a, b) => parseInt(b.budget.replace(/\D/g, '')) - parseInt(a.budget.replace(/\D/g, '')));
  //       break;
  //     case 'budget-low':
  //       result.sort((a, b) => parseInt(a.budget.replace(/\D/g, '')) - parseInt(b.budget.replace(/\D/g, '')));
  //       break;
  //     case 'deadline':
  //       result.sort((a, b) => new Date(a.deadline).getTime() - new Date(b.deadline).getTime());
  //       break;
  //   }

  //   setFilteredProjects(result);
  // }, [projects, searchTerm, statusFilter, sortBy]);

  const fetchAllJobs = async (data: any) => {
    const payload = {
      search: "",
      skillFilter: "",
      category: "",
      budget: "",
      PricingDetails: "",
      page: 1,
      limit: 10,
    };
    const updatedData = data ? data : payload;
    try {
      await fetchAllProject(user?.userId, user?.accessToken, updatedData);
    } catch (err) {
      console.log("Error:", err);
    }
  };

  const handlePageChange = (selectedPage: number) => {
    if (ref.current) {
      setFetchDataProps({ ...fetchDataProps, page: selectedPage });
      fetchAllJobs({ ...fetchDataProps, page: selectedPage });
    }
  };

  const handleSearchTerms = (e) => {
    setFetchDataProps({
      ...fetchDataProps,
      search: e.target.value,
    });
    const payload = {
      search: e.target.value,
      skillFilter: "",
      category: "",
      budget: "",
      PricingDetails: "",
      sortBy: "createdAt",
      order: "desc",
      page: 1,
      limit: 5,
    };
    fetchAllJobs(payload);
  };
  const debouncedFetchJobs = useCallback(debounce(handleSearchTerms, 500), []);

  useEffect(() => {
    fetchAllJobs(fetchDataProps);
    ref.current = true;
  }, []);

  return (
    <div className="container mx-auto p-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div>
          <h1 className="text-3xl font-bold">Projects</h1>
          <p className="text-muted-foreground">
            This list is tailored to your profile using AI-driven insights.
          </p>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search projects..."
            className="pl-8"
            value={searchTerm}
            onChange={(e) => {
              debouncedFetchJobs(e);
              setSearchTerm(e.target.value);
            }}
          />
        </div>

        <Select
          value={fetchDataProps.category}
          onValueChange={(value) => {
            setFetchDataProps({
              ...fetchDataProps,
              category: value === "all" ? "" : value,
              page: 1,
            });
            fetchAllJobs({ ...fetchDataProps, category: value });
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={"all"}>All</SelectItem>
            {CATEGORIES.map((category, idx) => {
              return (
                <div key={idx}>
                  <SelectItem value={category}>{category}</SelectItem>
                </div>
              );
            })}
          </SelectContent>
        </Select>

        <div>
          <Select
            value={fetchDataProps.budget}
            onValueChange={(value) => {
              setFetchDataProps({
                ...fetchDataProps,
                budget: value,
                page: 1,
              });
              fetchAllJobs({ ...fetchDataProps, budget: value });
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Budget" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Rates</SelectItem>
              <SelectItem value="0-25">$0 - $25</SelectItem>
              <SelectItem value="25-50">$25 - $50</SelectItem>
              <SelectItem value="50-100">$50 - $100</SelectItem>
              <SelectItem value="100+">$100+</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Select
            value={fetchDataProps.PricingDetails}
            onValueChange={(value) => {
              setFetchDataProps({
                ...fetchDataProps,
                PricingDetails: value === "all" ? "" : value,
                page: 1,
              });
              fetchAllJobs({
                ...fetchDataProps,
                PricingDetails: value === "all" ? "" : value,
              });
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Pricing model" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="dailyRate">Daily rate</SelectItem>
              <SelectItem value="fixedPrice">Fixed rate</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Select
            value={fetchDataProps.skillFilter}
            onValueChange={(value) => {
              setFetchDataProps({
                ...fetchDataProps,
                skillFilter: value === "all" ? "" : value,
                page: 1,
              });
              fetchAllJobs({
                ...fetchDataProps,
                skillFilter: value === "all" ? "" : value,
              });
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Skills" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {Skills.map((skill, idx) => {
                return (
                  <SelectItem key={idx} value={skill}>
                    {skill}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
      </div>
      {profile && profile?.results?.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {profile?.results?.filter((project) => project?.job?.projectvisibility)
            .map((project) => (
              <ProjectCard key={project.job._id} project={project.job} />
            ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <FileText className="mx-auto h-12 w-12 text-muted-foreground opacity-50" />
          <h3 className="mt-4 text-lg font-medium">No projects found</h3>
          <p className="text-muted-foreground">
            Try adjusting your search or filter criteria
          </p>
        </div>
      )}
      {profile?.results?.length ?
        <Pagination
          totalPages={Math.ceil(profile?.total / fetchDataProps?.limit)}
          onPageChange={handlePageChange}
          forcePage={(profile?.page || 1) - 1}
          className="mt-6"
        />
        : null
      }
    </div>
  );
};

export default Projects;
